import { useState } from "react";
import { STATUS } from "./schema";
import { permitUrgency } from "./utils/permits";

const DAY_MS = 24 * 60 * 60 * 1000;

function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  if (typeof ts === "string") return new Date(ts);
  if (typeof ts === "number") return new Date(ts);
  return null;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Derive urgent items for the morning briefing panel.
 * @param {Array<object>} bookings
 * @returns {Array<{id: string, kind: string, label: string, bookingId: string}>}
 */
export function deriveBriefing(bookings) {
  const now = new Date();
  const today = startOfDay(now);
  const tomorrow = new Date(today.getTime() + DAY_MS);
  const items = [];

  for (const b of bookings) {
    if (b.archivedAt) continue;

    // Events today
    const confirmed = toDate(b.confirmedDate);
    if (confirmed && confirmed >= today && confirmed < tomorrow) {
      items.push({
        id: `today-${b.id}`,
        kind: "today",
        label: `TODAY: ${b.eventName || b.name || "Event"}${b.venueName ? " @ " + b.venueName : ""}`,
        bookingId: b.id,
        tab: "details",
      });
      continue;
    }

    // No response > 7 days since submission, still in inquiry states
    const submitted = toDate(b.submittedAt);
    const inquiryStates = [STATUS.NEW_INQUIRY, STATUS.CONTACTED, STATUS.QUOTED];
    if (submitted && inquiryStates.includes(b.status)) {
      const daysOld = Math.floor((now - submitted) / DAY_MS);
      if (daysOld >= 7) {
        items.push({
          id: `stale-${b.id}`,
          kind: "stale",
          label: `${daysOld}d no response: ${b.name || b.email || "inquiry"}`,
          bookingId: b.id,
          tab: "details",
        });
      }
    }

    // Explicit overdue follow-up
    const follow = toDate(b.followUpDate);
    if (follow && follow < today && b.status !== STATUS.COMPLETED && b.status !== STATUS.DECLINED) {
      items.push({
        id: `followup-${b.id}`,
        kind: "followup",
        label: `Follow up overdue: ${b.name || b.eventName || b.email || "booking"}`,
        bookingId: b.id,
        tab: "details",
      });
    }

    // Upcoming event with no deposit
    if (confirmed && confirmed >= today) {
      const daysUntil = Math.floor((confirmed - today) / DAY_MS);
      if (daysUntil <= 14 && !b.depositPaidAt && b.status !== STATUS.DEPOSIT_RECEIVED && b.status !== STATUS.BOOKED) {
        items.push({
          id: `deposit-${b.id}`,
          kind: "deposit",
          label: `${daysUntil}d out, no deposit: ${b.eventName || b.name}`,
          bookingId: b.id,
          tab: "details",
        });
      }
    }

    // Permit urgency — include actionable detail so Dan knows what to do
    const permit = permitUrgency(b, now);
    if (permit === "urgent") {
      const action = !b.healthPermitStatus || b.healthPermitStatus === "pending_application"
        ? "Apply now — walk into the health dept"
        : b.healthPermitStatus === "applied"
        ? "Pay the fee — walk in within 2 business days"
        : "Permit needs attention";
      items.push({
        id: `permit-urgent-${b.id}`,
        kind: "permit_urgent",
        label: `🚨 ${b.eventName || b.name}: ${action}`,
        bookingId: b.id,
        tab: "logistics",
      });
    } else if (permit === "warning") {
      items.push({
        id: `permit-warning-${b.id}`,
        kind: "permit_warning",
        label: `Permit not yet paid (<7d out): ${b.eventName || b.name}`,
        bookingId: b.id,
        tab: "logistics",
      });
    }
  }

  return items;
}

const KIND_COLORS = {
  today:          { bg: "#fcb31f", fg: "#0d1833", icon: "📅" },
  stale:          { bg: "#ef537e", fg: "#ffffff", icon: "⏳" },
  followup:       { bg: "#f57600", fg: "#ffffff", icon: "📞" },
  deposit:        { bg: "#4aa4e1", fg: "#0a1a2e", icon: "💰" },
  permit_urgent:  { bg: "#c94444", fg: "#ffffff", icon: "🚨" },
  permit_warning: { bg: "#f57600", fg: "#ffffff", icon: "📋" },
};

export default function MorningBriefing({ bookings, onOpen }) {
  const items = deriveBriefing(bookings);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("jop_briefing_collapsed") === "1"; } catch { return false; }
  });
  if (items.length === 0) return null;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem("jop_briefing_collapsed", next ? "1" : "0"); } catch {}
  };

  return (
    <div style={{
      background: "#142548",
      border: "1px solid #ef537e",
      borderRadius: "12px",
      padding: collapsed ? "0.6rem 1rem" : "0.9rem 1rem",
      marginBottom: "1rem",
    }}>
      <button
        type="button"
        onClick={toggle}
        style={{
          background: "transparent",
          border: "none",
          color: "#ef537e",
          fontWeight: 700,
          fontSize: "0.8rem",
          letterSpacing: "0.04em",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          width: "100%",
          textAlign: "left",
          padding: 0,
          marginBottom: collapsed ? 0 : "0.5rem",
        }}
      >
        <span style={{ fontSize: "0.75rem" }}>{collapsed ? "▶" : "▼"}</span>
        <span>🌅 MORNING BRIEFING · {items.length} urgent</span>
      </button>
      {!collapsed && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          {items.map((item) => {
            const c = KIND_COLORS[item.kind] || KIND_COLORS.stale;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpen(item.bookingId, item.tab)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.6rem",
                  background: c.bg,
                  color: c.fg,
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.55rem 0.8rem",
                  textAlign: "left",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                }}
              >
                <span>{c.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                <span style={{ opacity: 0.7 }}>→</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
